import { z } from 'zod';

/** UUID string. */
export const Id = z.string().uuid();
export type Id = z.infer<typeof Id>;

/** Integer pence (FR-3.6). Never floats for money. */
export const Pence = z.number().int().safe();
export type Pence = z.infer<typeof Pence>;
/** Non-negative integer pence. */
export const PositivePence = Pence.nonnegative();

/** ISO calendar date `yyyy-mm-dd`. */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format yyyy-mm-dd');
export type IsoDate = z.infer<typeof IsoDate>;

/** ISO timestamp (with timezone). */
export const IsoDateTime = z.string().datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/** UK tax year label, e.g. `2026-27` (6 April – 5 April). */
export const TaxYear = z.string().regex(/^\d{4}-\d{2}$/, 'Use the format 2026-27');
export type TaxYear = z.infer<typeof TaxYear>;

/** Standard API error envelope (ARCHITECTURE §4). */
export const ApiErrorBody = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

export const ERROR_CODES = [
  'validation_error',
  'unauthenticated',
  'mfa_required',
  'read_only',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'csrf',
  'internal',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Cursor/offset pagination query. */
export const PaginationQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof PaginationQuery>;

export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  });
export type Paginated<T> = { items: T[]; total: number; limit: number; offset: number };

export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;

/** Params for any workspace-scoped route. */
export const WorkspaceParams = z.object({ workspaceId: Id });
export type WorkspaceParams = z.infer<typeof WorkspaceParams>;
export const WorkspaceEntityParams = WorkspaceParams.extend({ id: Id });
export type WorkspaceEntityParams = z.infer<typeof WorkspaceEntityParams>;

/** Response of `ctx.exports.renderPdf()` / export endpoints. */
export const ExportResult = z.object({
  exportId: Id,
  downloadUrl: z.string(),
  filename: z.string(),
});
export type ExportResult = z.infer<typeof ExportResult>;
